import React from "react";
import { Box, Text, Tooltip } from "@chakra-ui/react";
import {
  formatThresholdLabel,
  getMoistureIndexSegments,
  getMoistureIndexThresholds,
  MOISTURE_INDEX_LEGEND_MAX,
  MOISTURE_INDEX_LEGEND_MIN,
  MoistureIndexThresholds,
} from "../utils/colorUtils/mapColorUtils";

export { getMoistureIndexThresholds };
export type { MoistureIndexThresholds };

export const MoistureIndexLegend = ({
  thresholds = {},
}: {
  thresholds?: MoistureIndexThresholds;
}) => {
  const segments = getMoistureIndexSegments(thresholds);
  const legendRange = MOISTURE_INDEX_LEGEND_MAX - MOISTURE_INDEX_LEGEND_MIN;
  const toLegendPercent = (value: number) => {
    const clamped = Math.min(Math.max(value, MOISTURE_INDEX_LEGEND_MIN), MOISTURE_INDEX_LEGEND_MAX);
    return `${((clamped - MOISTURE_INDEX_LEGEND_MIN) / legendRange) * 100}%`;
  };
  const boundaryMarkers = [
    { value: MOISTURE_INDEX_LEGEND_MIN, label: "<0" },
    { value: 0, label: "0.00" },
    thresholds.deficit !== undefined
      ? { value: thresholds.deficit, label: formatThresholdLabel(thresholds.deficit) }
      : null,
    {
      value: thresholds.management ?? 0.5,
      label: formatThresholdLabel(thresholds.management ?? 0.5),
    },
    thresholds.upperManagement !== undefined
      ? {
          value: thresholds.upperManagement,
          label: formatThresholdLabel(thresholds.upperManagement),
        }
      : null,
    { value: 1, label: "1.00" },
    { value: MOISTURE_INDEX_LEGEND_MAX, label: ">1" },
  ]
    .filter((m): m is { value: number; label: string } => m !== null)
    .filter((m, i, arr) => arr.findIndex((c) => c.value === m.value) === i);
  const regionRotationThreshold = 0.2;
  const shouldRotate = segments.some(
    (s) => (s.to - s.from) / legendRange < regionRotationThreshold,
  );
  return (
    <Tooltip label="Estimated Plant Available Water (PAW) in relationship to recommended moisture levels for this crop.">
      <Box display="flex" flexDirection="column">
        <Box position="relative" pt={4} pb={5}>
          <svg
            width="100%"
            height="20"
            viewBox={`${MOISTURE_INDEX_LEGEND_MIN} 0 ${legendRange} 1`}
            preserveAspectRatio="none"
          >
            <defs>
              {segments.map(
                (segment, index) =>
                  (segment.gradientStartColor || segment.gradientEndColor) && (
                    <linearGradient
                      key={`mi-grad-${index}`}
                      id={`mi-grad-${index}`}
                      x1="0"
                      x2="1"
                      y1="0"
                      y2="0"
                    >
                      <stop offset="0%" stopColor={segment.gradientStartColor ?? segment.color} />
                      <stop offset="55%" stopColor={segment.color} />
                      <stop offset="100%" stopColor={segment.gradientEndColor ?? segment.color} />
                    </linearGradient>
                  ),
              )}
            </defs>
            {segments.map((segment, index) => (
              <rect
                key={`mi-rect-${index}`}
                x={segment.from}
                y="0"
                width={segment.to - segment.from}
                height="1"
                fill={
                  segment.gradientStartColor || segment.gradientEndColor
                    ? `url(#mi-grad-${index})`
                    : segment.color
                }
              />
            ))}
          </svg>
          {boundaryMarkers.map((marker) => (
            <Box
              key={`mi-marker-${marker.label}`}
              position="absolute"
              left={toLegendPercent(marker.value)}
              top="-5px"
              transform="translateX(-50%)"
              pointerEvents="none"
            >
              <Text fontSize="xs" whiteSpace="nowrap">
                {marker.label}
              </Text>
              {marker.value !== MOISTURE_INDEX_LEGEND_MIN &&
                marker.value !== MOISTURE_INDEX_LEGEND_MAX && (
                  <Box width="1px" height="9px" bg="gray.500" mx="auto" />
                )}
            </Box>
          ))}
          {segments.map((segment, index) => (
            <Text
              key={`mi-region-${index}`}
              position="absolute"
              left={toLegendPercent((segment.from + segment.to) / 2)}
              top="100%"
              mt={-1}
              transform={shouldRotate ? "translateX(-50%) rotate(-35deg)" : "translateX(-50%)"}
              transformOrigin="top center"
              fontSize="xs"
              whiteSpace="nowrap"
              pointerEvents="none"
            >
              {segment.regionLabel}
            </Text>
          ))}
        </Box>
        <Text mt={5} fontSize="sm" textAlign="center" fontWeight={600}>
          Plant Available Water (PAW)
        </Text>
      </Box>
    </Tooltip>
  );
};
